/**
 * Modal for creating a new requirement.
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  message,
  Button,
  Row,
  Col,
} from 'antd';
import { createRequirement, fetchDeliveryStages, listRequirements, listUsers, listProjectTags, listProjectTags1, listProjectTags2 } from '@/api/requirements';
import type { RequirementUser } from '@/api/requirements';
import { useAuthStore } from '@/store/authStore';
import {
  PRIORITY_OPTIONS,
  getCategoryOptions,
  localizeOptions,
} from '@/constants/requirementOptions';
import { useT } from '@/i18n';
import type { RequirementCreate } from '@/types';

const { OptGroup } = Select;

// Standard delivery stages
const STANDARD_STAGES = [
  'BSW01(台架验证)',
  'BSW02(首车闭环)',
  'BSW03(性能优化)',
  'BSW04(故障处理)',
  'BSW05(量产锁版)',
];

const STANDARD_STAGE_OPTIONS = STANDARD_STAGES.map(stage => ({
  value: stage,
  label: stage,
}));

// Hardware options (from BVP)
const HARDWARE_OPTIONS = [
  { value: 'Mcu', label: 'Mcu' },
  { value: 'Soc', label: 'Soc' },
  { value: 'Adcu', label: 'Adcu' },
  { value: 'Switch', label: 'Switch' },
];

interface RequirementCreateModalProps {
  open: boolean;
  projectId: number;
  subcategory: string;
  onClose: () => void;
  onSuccess: () => void;
  isMainlinePool?: boolean;
  otherMainlineProjects?: { id: number; name: string }[];
}

const RequirementCreateModal: React.FC<RequirementCreateModalProps> = ({
  open,
  projectId,
  subcategory,
  onClose,
  onSuccess,
  isMainlinePool,
  otherMainlineProjects,
}) => {
  const { t, lang } = useT();
  const [form] = Form.useForm();
  const deliveryStageValue = Form.useWatch('deliveryStage', form);
  const [loading, setLoading] = useState(false);
  const [deliveryStages, setDeliveryStages] = useState<string[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [deliveryStageOpen, setDeliveryStageOpen] = useState(false);
  const [verificationMethods, setVerificationMethods] = useState<string[]>([]);
  const [users, setUsers] = useState<RequirementUser[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const [projectTags1, setProjectTags1] = useState<string[]>([]);
  const [projectTags2, setProjectTags2] = useState<string[]>([]);
  const [deliverableOptions, setDeliverableOptions] = useState<string[]>([]);

  const { user } = useAuthStore();

  // Fetch delivery stages and verification methods when modal opens
  useEffect(() => {
    if (!open) return;

    // 1. Fetch delivery stages
    setLoadingStages(true);
    fetchDeliveryStages(projectId)
      .then(data => {
        setDeliveryStages(data.delivery_stages);
      })
      .catch(() => {
        message.warning(t('无法加载项目阶段，已显示标准阶段'));
        setDeliveryStages([]);
      })
      .finally(() => {
        setLoadingStages(false);
      });

    // 2. Fetch existing requirements to extract verification methods and deliverables
    listRequirements(projectId, subcategory)
      .then(data => {
        const methods = new Set<string>();
        const deliverables = new Set<string>();
        data.requirements.forEach(req => {
          if (req.verificationMethod) methods.add(req.verificationMethod);
          if (req.deliverables) req.deliverables.split(/[,，;；\n]/).map(s => s.trim()).filter(Boolean).forEach(d => deliverables.add(d));
        });
        if (!methods.has('检查')) methods.add('检查');
        if (!methods.has('测试')) methods.add('测试');
        setVerificationMethods(Array.from(methods).sort());
        setDeliverableOptions(Array.from(deliverables).sort());
      })
      .catch(() => {
        setVerificationMethods(['检查', '测试']);
      });

    // 3. Fetch users for owner dropdown
    listUsers().then(setUsers).catch(() => setUsers([]));

    // 4. Fetch existing TAG/TAG1/TAG2 values used in this project
    listProjectTags(projectId).then(setProjectTags).catch(() => setProjectTags([]));
    listProjectTags1(projectId).then(setProjectTags1).catch(() => setProjectTags1([]));
    listProjectTags2(projectId).then(setProjectTags2).catch(() => setProjectTags2([]));

    // 5. Set initial form values
    form.setFieldsValue({
      subcategory,
      owner: user?.username || '',
    });
  }, [open, projectId, subcategory, form, user, t]);


  // Build grouped options
  const projectSpecificOptions = deliveryStages
    .filter(stage => !STANDARD_STAGES.includes(stage))
    .map(stage => ({ value: stage, label: stage }));

  // Get category options based on current subcategory, supporting custom input
  const baseCategoryOptions = localizeOptions(getCategoryOptions(subcategory), t);
  const categoryOptions = categorySearch && !baseCategoryOptions.find(o => o.value === categorySearch)
    ? [...baseCategoryOptions, { value: categorySearch, label: categorySearch }]
    : baseCategoryOptions;

  const handleCreate = async () => {
    const values = await form.validateFields();

    // Convert deliveryStage array to string (from mode="tags")
    if (Array.isArray(values.deliveryStage)) {
      values.deliveryStage = values.deliveryStage[0] || '';
    }

    if (!values.deliveryStage) {
      message.error(t('交付阶段不能为空'));
      return;
    }

    setLoading(true);
    try {
      const createData: RequirementCreate & { target_project_ids?: number[] } = {
        subcategory,
        description: values.description.trim(),
        deliveryStage: values.deliveryStage,
        hardware: values.hardware,
        priority: values.priority,  // 必填，直接传递
        category: values.category || undefined,
        category2: values.category2?.trim() || undefined,
        owner: values.owner?.trim() || undefined,
        deliverables: values.deliverables?.trim() || undefined,
        verificationMethod: values.verificationMethod || undefined,
        validationCriteria: values.validationCriteria?.trim() || undefined,
        attachment: values.attachment?.trim() || undefined,
        remark: values.remark?.trim() || undefined,  // 新增备注
        bsp_tag: values.bsp_tag?.trim() || undefined,
        tag1: values.tag1?.trim() || undefined,
        tag2: values.tag2?.trim() || undefined,
      };
      if (isMainlinePool && values.target_project_ids?.length) {
        createData.target_project_ids = values.target_project_ids;
      }

      const result = await createRequirement(projectId, createData as RequirementCreate);
      message.success(t('需求已创建'));
      if ((result as any)?.synced_projects?.length) {
        message.info(t('已同步到主线项目：{projects}', { projects: (result as any).synced_projects.join(lang === 'en' ? ', ' : '、') }));
      }
      form.resetFields();

      // Add a small delay to ensure backend has committed the data
      // before the parent component refetches the list
      await new Promise(resolve => setTimeout(resolve, 500));

      onSuccess();
      onClose();
    } catch (error: any) {
      if (error.response?.status === 400) {
        const errorMsg = error.response.data?.message || t('字段验证失败');
        message.error(errorMsg);
      } else if (error.response?.status === 403) {
        message.error(t('您没有权限在该项目中创建需求'));
      } else if (error.response?.status === 404) {
        message.error(t('项目配置错误，请联系管理员'));
      } else if (error.response?.status === 500) {
        const errorMsg = error.response.data?.message || t('服务器内部错误，请查看服务器日志');
        message.error(errorMsg);
      } else if (error.response?.status === 502) {
        message.error(t('BVP 服务暂时不可用，请稍后重试'));
      } else {
        console.error('创建需求错误:', error);
        message.error(t('创建失败，请稍后重试'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('新增需求')}
      open={open}
      onCancel={onClose}
      width={800}
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingTop: 8 } }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('取消')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleCreate}
        >
          {t('创建')}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        size="small"
        initialValues={{
          subcategory,
        }}
        style={{ rowGap: 0 }}
      >
        {/* 第一行：子类别（只读，占满） */}
        <Form.Item label={t('需求子类别')} name="subcategory" style={{ marginBottom: 8 }}>
          <Input disabled />
        </Form.Item>

        {/* 需求描述（全宽） */}
        <Form.Item
          label={t('需求描述')}
          name="description"
          style={{ marginBottom: 8 }}
          rules={[
            { required: true, message: t('请输入需求描述') },
            {
              validator: (_, value) => {
                if (value && value.trim().length < 10) {
                  return Promise.reject(new Error(t('需求描述至少需要10个字符')));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 6 }}
            placeholder={t('请详细描述需求...')}
            showCount
            maxLength={5000}
          />
        </Form.Item>

        {/* 第二行：交付阶段 + 硬件 + 优先级 + 分类（各 1/4） */}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              label={t('交付阶段')}
              name="deliveryStage"
              style={{ marginBottom: 8 }}
              rules={[
                { required: true, message: t('请选择交付阶段') },
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(new Error(t('交付阶段不能为空')));
                    }
                    const stageValue = Array.isArray(value) ? value[0] : value;
                    if (stageValue && stageValue.length > 20) {
                      return Promise.reject(
                        new Error(t('不能超过20个字符（当前：{count}）', { count: stageValue.length }))
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={(() => {
                const currentValue = Array.isArray(deliveryStageValue)
                  ? deliveryStageValue[0]
                  : deliveryStageValue;
                const length = currentValue?.length || 0;
                return (
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {t('{count}/20 · 只选一个', { count: length })}
                  </span>
                );
              })()}
            >
              <Select
                mode="tags"
                maxCount={1}
                maxLength={20}
                allowClear
                loading={loadingStages}
                disabled={loadingStages}
                placeholder={t('选择或输入')}
                open={deliveryStageOpen}
                onOpenChange={setDeliveryStageOpen}
                onSelect={() => setDeliveryStageOpen(false)}
              >
                <OptGroup label={t('标准阶段')}>
                  {STANDARD_STAGE_OPTIONS.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </OptGroup>
                {projectSpecificOptions.length > 0 && (
                  <OptGroup label={t('项目特定阶段')}>
                    {projectSpecificOptions.map(opt => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={t('硬件')}
              name="hardware"
              style={{ marginBottom: 8 }}
              rules={[{ required: true, message: t('请选择硬件') }]}
            >
              <Select placeholder={t('选择硬件类型')}>
                {HARDWARE_OPTIONS.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={t('优先级')}
              name="priority"
              style={{ marginBottom: 8 }}
              rules={[{ required: true, message: t('请选择优先级') }]}
            >
              <Select placeholder={t('请选择优先级')} allowClear>
                {localizeOptions(PRIORITY_OPTIONS, t).map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={t('一级分类')}
              name="category"
              style={{ marginBottom: 8 }}
              rules={[{ required: true, message: t('请选择一级分类') }]}
            >
              <Select
                placeholder={t('选择或输入一级分类')}
                allowClear
                showSearch
                onSearch={setCategorySearch}
                onSelect={() => setCategorySearch('')}
                filterOption={(input, option) => {
                  const label = typeof option?.label === 'string' ? option.label : '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {categoryOptions.map(opt => (
                  <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* 第三行：二级分类 + 责任人 + 验证方法 + 交付物（各 1/4） */}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label={t('二级分类')} name="category2" style={{ marginBottom: 8 }}>
              <Input placeholder={t('请输入二级分类（可选）')} allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={t('责任人')} name="owner" style={{ marginBottom: 8 }}>
              <Select
                showSearch
                allowClear
                placeholder={t('选择责任人（默认当前用户）')}
                filterOption={(input, option) => {
                  const label = typeof option?.label === 'string' ? option.label : '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={users.map(u => ({
                  value: u.username,
                  label: u.displayName ? `${u.displayName} (${u.username})` : u.username,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={t('验证方法')}
              name="verificationMethod"
              style={{ marginBottom: 8 }}
              rules={[{ required: true, message: t('请选择验证方法') }]}
            >
              <Select placeholder={t('选择验证方法')} allowClear>
                {verificationMethods.map(method => (
                  <Select.Option key={method} value={method}>
                    {t(method)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={t('交付物')} name="deliverables" style={{ marginBottom: 8 }}>
              <AutoComplete
                placeholder={t('选择或输入交付物（可选）')}
                allowClear
                options={deliverableOptions.map(d => ({ value: d }))}
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 验证准则（全宽） */}
        <Form.Item label={t('验证准则')} name="validationCriteria" style={{ marginBottom: 8 }}>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 5 }}
            placeholder={t('请输入验证准则（可选）')}
            maxLength={5000}
            showCount
          />
        </Form.Item>

        {/* 设计文档（全宽） */}
        <Form.Item label={t('设计文档')} name="attachment" style={{ marginBottom: 8 }}>
          <Input
            placeholder={t('请输入设计文档链接（可选）')}
            allowClear
          />
        </Form.Item>

        {/* 备注（全宽） */}
        <Form.Item label={t('备注')} name="remark" style={{ marginBottom: 8 }}>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 5 }}
            placeholder={t('请输入需求备注信息（可选）')}
            maxLength={5000}
            showCount
          />
        </Form.Item>

        {/* 自定义TAG1 + TAG2 + TAG3（各 1/3，放最下面） */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label={t('自定义TAG1')} name="bsp_tag" style={{ marginBottom: 0 }}>
              <AutoComplete
                placeholder={t('选择或输入（可选）')}
                allowClear
                maxLength={100}
                options={projectTags.map(t => ({ value: t }))}
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('自定义TAG2')} name="tag1" style={{ marginBottom: 0 }}>
              <AutoComplete
                placeholder={t('选择或输入（可选）')}
                allowClear
                maxLength={100}
                options={projectTags1.map(t => ({ value: t }))}
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('自定义TAG3')} name="tag2" style={{ marginBottom: 0 }}>
              <AutoComplete
                placeholder={t('选择或输入（可选）')}
                allowClear
                maxLength={100}
                options={projectTags2.map(t => ({ value: t }))}
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {isMainlinePool && otherMainlineProjects && otherMainlineProjects.length > 0 && (
          <Form.Item
            label={t('同步到主线项目')}
            name="target_project_ids"
            style={{ marginBottom: 8, marginTop: 4 }}
            tooltip={t('选中的主线项目将同时新增该需求（可选）')}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder={t('选择需要同步的主线项目（可选）')}
              options={otherMainlineProjects.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default RequirementCreateModal;
